import { Link } from "react-router-dom";

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="cookie-policy-page">
      <div className="max-w-[800px] mx-auto px-6 lg:px-8 py-16 sm:py-20">
        <h1 className="text-[32px] sm:text-[40px] font-normal text-[#202124] leading-tight mb-2">Cookie Policy</h1>
        <p className="text-[13px] text-[#80868b] mb-10">Last updated: March 29, 2026</p>

        <div className="prose-eamico space-y-8 text-[15px] text-[#3c4043] leading-relaxed">

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">1. Introduction</h2>
            <p>
              Eamico Inc. ("Eamico," "we," "us," or "our"), a Federal Canadian corporation (Corporation Number 1781131-4, Business Number 792360372) and subsidiary of Aikaro Technologies Corp., uses cookies and similar tracking technologies on our website and services. This Cookie Policy explains what cookies are, how we use them, your choices regarding cookies, and further information about cookies.
            </p>
            <p className="mt-3">
              This policy should be read together with our <Link to="/privacy" className="text-[#ff9a4d] hover:underline">Privacy Policy</Link>, which explains how we handle personal information.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">2. What Are Cookies?</h2>
            <p>
              Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work efficiently, provide a better user experience, and supply information to website owners. Cookies can be "persistent" (remain on your device until deleted or expired) or "session" (deleted when you close your browser).
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">3. Types of Cookies We Use</h2>

            <div className="mt-4 space-y-5">
              <div className="bg-[#f8f9fa] rounded-xl border border-[#e8eaed] p-5">
                <h3 className="text-[16px] font-medium text-[#202124] mb-2">Strictly Necessary Cookies</h3>
                <p className="text-[14px]">Essential for the website to function properly. These cannot be disabled. They include cookies for security, session management, and load balancing.</p>
                <p className="text-[13px] text-[#80868b] mt-2">Legal basis: Legitimate interest / necessary for service provision</p>
              </div>

              <div className="bg-[#f8f9fa] rounded-xl border border-[#e8eaed] p-5">
                <h3 className="text-[16px] font-medium text-[#202124] mb-2">Functional Cookies</h3>
                <p className="text-[14px]">Enable enhanced functionality and personalization, such as remembering your preferences, language selection, and login status.</p>
                <p className="text-[13px] text-[#80868b] mt-2">Legal basis: Consent</p>
              </div>

              <div className="bg-[#f8f9fa] rounded-xl border border-[#e8eaed] p-5">
                <h3 className="text-[16px] font-medium text-[#202124] mb-2">Analytics Cookies</h3>
                <p className="text-[14px]">Help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our services.</p>
                <p className="text-[13px] text-[#80868b] mt-2">Legal basis: Consent</p>
              </div>

              <div className="bg-[#f8f9fa] rounded-xl border border-[#e8eaed] p-5">
                <h3 className="text-[16px] font-medium text-[#202124] mb-2">Marketing Cookies</h3>
                <p className="text-[14px]">Used to track visitors across websites to display relevant advertisements. We currently do not use marketing cookies but may do so in the future with appropriate notice and consent mechanisms.</p>
                <p className="text-[13px] text-[#80868b] mt-2">Legal basis: Consent (when implemented)</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">4. Similar Technologies</h2>
            <p>In addition to cookies, we may use:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li><strong>Local Storage:</strong> Similar to cookies but with larger storage capacity, used to store preferences and session data</li>
              <li><strong>Pixel Tags / Web Beacons:</strong> Tiny graphics used to track user activity and measure the effectiveness of communications</li>
              <li><strong>Fingerprinting:</strong> We do not use browser fingerprinting for tracking purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">5. Your Cookie Choices</h2>

            <h3 className="text-[16px] font-medium text-[#202124] mt-4 mb-2">5.1 Browser Settings</h3>
            <p>Most web browsers allow you to manage cookies through browser settings. You can:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li>Delete all cookies from your browser</li>
              <li>Block all cookies by default</li>
              <li>Allow only first-party cookies</li>
              <li>Clear cookies when you close your browser</li>
            </ul>
            <p className="mt-2 text-[14px] text-[#5f6368]">Note: Blocking certain cookies may affect the functionality of our website.</p>

            <h3 className="text-[16px] font-medium text-[#202124] mt-4 mb-2">5.2 Opt-Out Links</h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Google Analytics:</strong> <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#ff9a4d] hover:underline">Google Analytics Opt-Out</a></li>
              <li><strong>NAI (U.S.):</strong> <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer" className="text-[#ff9a4d] hover:underline">Network Advertising Initiative Opt-Out</a></li>
              <li><strong>DAA (U.S.):</strong> <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer" className="text-[#ff9a4d] hover:underline">Digital Advertising Alliance Opt-Out</a></li>
              <li><strong>DAAC (Canada):</strong> <a href="https://youradchoices.ca/choices/" target="_blank" rel="noopener noreferrer" className="text-[#ff9a4d] hover:underline">Digital Advertising Alliance of Canada</a></li>
            </ul>

            <h3 className="text-[16px] font-medium text-[#202124] mt-4 mb-2">5.3 Do Not Track</h3>
            <p>We honor Do Not Track (DNT) browser signals. When DNT is enabled, we will not deploy non-essential cookies or tracking technologies.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">6. Compliance</h2>

            <h3 className="text-[16px] font-medium text-[#202124] mt-4 mb-2">6.1 Canada (PIPEDA & CASL)</h3>
            <p>In compliance with Canadian privacy legislation, we obtain meaningful consent before placing non-essential cookies and provide clear information about their purpose and duration.</p>

            <h3 className="text-[16px] font-medium text-[#202124] mt-4 mb-2">6.2 United States (State Privacy Laws)</h3>
            <p>For users in California (CCPA/CPRA), Virginia (VCDPA), Colorado (CPA), Connecticut (CTDPA), and other states with privacy legislation, we provide opt-out mechanisms and honor universal opt-out signals as required by law.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">7. Updates to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in our practices or applicable laws. We will post the updated policy on our website with a new "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">8. Contact Us</h2>
            <div className="bg-[#f8f9fa] rounded-xl border border-[#e8eaed] p-6 mt-3">
              <p className="font-medium text-[#202124] mb-3">Eamico Inc.</p>
              <p>Subsidiary of Aikaro Technologies Corp.</p>
              <p>Federal Canadian Corporation</p>
              <p>Corporation Number: 1781131-4</p>
              <p>Business Number: 792360372</p>
              <div className="mt-4 space-y-1">
                <p><strong>Privacy Inquiries:</strong> <a href="mailto:privacy@aikarotech.com" className="text-[#ff9a4d] hover:underline">privacy@aikarotech.com</a></p>
                <p><strong>Legal Inquiries:</strong> <a href="mailto:legal@aikarotech.com" className="text-[#ff9a4d] hover:underline">legal@aikarotech.com</a></p>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
